// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for MeetingAttendance entity
 */
type MeetingAttendanceConfig = BaseMetricConfig;

/**
 * MeetingAttendance Entity - Encapsulates all meeting attendance-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the meeting attendance ecosystem metric.
 */
export class MeetingAttendance extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'meeting_attendance' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: MeetingAttendanceConfig = {
    metricType: 'meeting_attendance',
    display: {
      displayName: 'Meeting Attendance',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of meetings for this project did employees from our company attend?',
        scoringRules: [
          'Each 0.1% of meetings attended for the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 meetings attended = 0 points',
          '0.000001 - 0.1% of meetings attended = 1.1 point',
          '0.2% of meetings attended = 1.2 points',
          '2.5% of meetings attended = 3.5 points',
          '20.0% of meetings attended = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company has no meeting attendance for this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Employees from our company attended {percentage}% of all committee meetings for this project.',
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for meeting attendance
   *
   * Note: Unlike LF-specific ecosystem metrics, Meeting Attendance does NOT check isLfProject
   * because this is a project-level metric where meeting data can exist for any project,
   * regardless of LF membership status.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getBottomSummary(percentage: string, allTimeTotal: number, _isLfProject: boolean, level: ScoreLevel, _projectType?: string | null): MetricBottomSummary {
    // Note: We intentionally do NOT check isLfProject here because Meeting Attendance
    // is a project-level metric that can exist for any project.

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    // Use pre-calculated level from backend
    const styles = this.getStylesByLevel(level);

    const description = numericValue === 0
      ? this.CONFIG.bottomSummary.zeroValue.description
      : this.formatDescription(this.CONFIG.bottomSummary.positiveValue.descriptionTemplate, {percentage: percentageValue});

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
